import { AppDispatch, RootStateType } from '@Store';
import dayjs, { Dayjs } from 'dayjs';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setIsWarehouseSelectedINFedX } from '@Reducers/Warehouse';
import { onForward, PaymentTypeOptions } from './constants';
import { useFormik } from 'formik';
import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import SBDropdown from '@Components/Dropdown';
import SBCountryDropdown from '@Components/Dropdown/Country';
import { vendorAccountType } from '@Reducers/AdminShipment/Type';
import SbTextField from '@Components/Textfield';
import DatePicker from '@Components/DatePicker';
import SbButton from '@Components/Button';
import { downloadCarrierLabel } from '@Reducers/ShipmentHistory/actions';
import * as Yup from 'yup';
import TextField from '@mui/material/TextField';

interface propsTypes {
  onClickForward: () => void;
  onClose?: () => void;
  initialStates: any;
  isEditable?: boolean;
  showFedExDetails: boolean;
  btnText: string;
}

export interface BluedartInformation {
  type: string;
  accountNo: string;
  countryOfManufacture: string;
  shipperTaxId: string;
  invoiceDate: Dayjs | null;
  productType: string;
  paymentType: string;
  invoiceNumber: string;
  pickupTime: string;
}

const bluedartInformationSchema = Yup.object().shape({
  type: Yup.string().trim().required('Type is required'),
  countryOfManufacture: Yup.string().trim().required('Country of manufacture is required'),
  invoiceDate: Yup.date().required('Invoice Date is required').nullable(),
  pickupTime: Yup.string()
    .required('Pickup Time is required')
    .matches(/^([01]?[0-9]|2[0-3])[0-5][0-9]$/, 'Invalid time format (HHMM)'),
  // .test(
  //   'pickup-time-validation',
  //   "Pickup time must be after current time for today's invoice date",
  //   function (value) {
  //     const { invoiceDate } = this.parent;
  //     if (!invoiceDate || !value) return true; // if no date or no pickup time, skip this validation

  //     const selectedDate = dayjs(invoiceDate);
  //     const today = dayjs();

  //     if (selectedDate.isSame(today, 'day')) {
  //       const now = dayjs();
  //       const currentMinutes = now.hour() * 60 + now.minute();

  //       // Convert pickupTime (e.g., '1715') into minutes
  //       const pickupHours = parseInt(value.substring(0, 2), 10);
  //       const pickupMinutes = parseInt(value.substring(2, 4), 10);
  //       const pickupTotalMinutes = pickupHours * 60 + pickupMinutes;

  //       // Pickup time must be at least 15 minutes after current time
  //       return pickupTotalMinutes >= currentMinutes + 15;
  //     }

  //     return true; // if not today, no restriction
  //   },
  // ),
  subProductCode: Yup.array().required('Sub Product Code is required'),
  invoiceNumber: Yup.string().trim().required('Invoice number is required'),
  accountNo: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Account value is required'),
        display: Yup.string().required('Account display is required'),
      }),
    )
    .min(1, 'At least one account number is required')
    .required('Account number is required'),
});

const getDefaultAccountNo = (accountNo: string, accountOptions: any[]) => {
  if (!accountNo || !accountOptions) return [];

  // Find the account option that starts with the accountNo followed by '-'
  const matchedAccount = accountOptions.find(option => option.value.startsWith(`${accountNo}-`));

  return matchedAccount ? [matchedAccount] : [];
};

const BlueDartInformation = (props: propsTypes) => {
  const dispatch = useDispatch<AppDispatch>();
  const { onClickForward, onClose, initialStates, isEditable, showFedExDetails, btnText } = props;
  const { shipmentById, vendersAccount, bluedartDetails } = useSelector((state: RootStateType) => state.adminShipment);
  const { isWarehouseSelectedINFedX } = useSelector((state: RootStateType) => state.Warehouse);
  const accountOptions = useMemo(
    () =>
      vendersAccount?.map((item: vendorAccountType) => ({
        value: `${item.accountNo}-${item._id}`,
        display: item.name,
      })),
    [vendersAccount],
  );

  const { values, handleChange, setFieldValue, errors, handleSubmit } = useFormik<any>({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      ...initialStates,
      invoiceNumber: shipmentById?._packageId.invoiceNumber,
      accountNo: getDefaultAccountNo(initialStates?.accountNo, accountOptions),
    },
    validationSchema: bluedartInformationSchema,
    onSubmit(values) {
      if (isWarehouseSelectedINFedX === '' || isWarehouseSelectedINFedX === 'false') {
        dispatch(setIsWarehouseSelectedINFedX('false'));
      } else {
        onForward({
          values,
          shipmentNumber: shipmentById?.shipmentNumber || '',
          dispatch,
          nextFunc: onClickForward,
        });
      }
    },
  });

  return (
    <div>
      {showFedExDetails && (
        <>
          <Paper
            variant="outlined"
            sx={{
              my: '2rem',
              p: '2rem',
            }}
          >
            <Grid container item xs={12}>
              <Grid container item mb={3}>
                <Grid item xs={12} md={6}>
                  <Typography className="font-medium l-text pb-3">Bluedart Information</Typography>{' '}
                </Grid>
              </Grid>
              <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
                <Grid container item columnSpacing={10} rowSpacing={2}>
                  <Grid item xs={12} md={6}>
                    <SbTextField
                      disabled={!isEditable}
                      label="Invoice Number"
                      required
                      value={values.invoiceNumber ?? shipmentById?._packageId.invoiceNumber}
                      placeholder="12345678"
                      onChange={handleChange}
                      name="invoiceNumber"
                      error={errors.invoiceNumber?.toString() as string}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <SBCountryDropdown
                      disabled={!isEditable}
                      required
                      label="Country of Manufacture"
                      value={values?.countryOfManufacture}
                      options={undefined}
                      onChange={value => {
                        setFieldValue('countryOfManufacture', value[0]);
                      }}
                      error={errors?.countryOfManufacture as string}
                    />
                  </Grid>
                </Grid>
                <Grid container item columnSpacing={10} rowSpacing={2}>
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      required
                      label="Invoice Date"
                      value={values?.invoiceDate || null}
                      handleChange={value => {
                        setFieldValue('invoiceDate', value);
                      }}
                      minDate={dayjs()}
                      disabled={!isEditable}
                      error={errors.invoiceDate as string}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <SBDropdown
                      disabled={!isEditable}
                      id="subProductCode"
                      label="Sub Product Code"
                      required
                      placeholder="Select "
                      name="subProductCode"
                      value={[values.subProductCode]}
                      options={PaymentTypeOptions}
                      onChange={value => {
                        setFieldValue('subProductCode', value);
                      }}
                      error={errors.subProductCode as string}
                    />
                  </Grid>
                </Grid>
                <Grid container item columnSpacing={10} rowSpacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body1" component="label" sx={{ fontSize: '14px', fontWeight: 500 }}>
                        Pickup Time *
                      </Typography>
                      <TextField
                        type="time"
                        value={
                          values.pickupTime
                            ? `${values.pickupTime.substring(0, 2)}:${values.pickupTime.substring(2)}`
                            : ''
                        }
                        onChange={e => {
                          const time = e.target.value.replace(/:/g, '');
                          setFieldValue('pickupTime', time);
                        }}
                        disabled={!isEditable}
                        required
                        error={!!errors.pickupTime}
                        helperText={(errors.pickupTime as string) || 'Select time in 24hr format'}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          step: 300, // 5 min intervals
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <SBDropdown
                      disabled={!isEditable}
                      id="accountNo"
                      label="Bluedart Account"
                      required
                      placeholder="Select"
                      name="accountNo"
                      value={
                        Array.isArray(values?.accountNo)
                          ? values.accountNo.map((item: { value: any }) => item.value)
                          : values.accountNo
                          ? [values.accountNo]
                          : []
                      }
                      options={accountOptions}
                      onChange={(_, selectedObj) => {
                        setFieldValue('accountNo', selectedObj);
                      }}
                      error={errors.accountNo as string}
                    />
                  </Grid>
                </Grid>
                <Divider className="mt-3 mb-3" />
                <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
                  <Grid container item sx={{ gap: '1rem' }}>
                    <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                      <SbButton
                        disabled={!isEditable}
                        variant="contained"
                        sx={{ borderRadius: '4px !important' }}
                        className="w-full"
                        onClick={_event => {
                          handleSubmit();
                        }}
                      >
                        {btnText}
                      </SbButton>
                    </Grid>
                    {onClose && (
                      <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                        <SbButton
                          sx={{ borderRadius: '4px !important' }}
                          className="w-full"
                          variant="outlined"
                          onClick={onClose}
                        >
                          Cancel
                        </SbButton>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </>
      )}
      {bluedartDetails?.response && (
        <Grid container item>
          <Grid item xs={'auto'} border={'1px solid'} className="border-grey font-medium md-text p-2" borderRadius={1}>
            This shipment has been forwarded.
            <Box
              component={'span'}
              className="font-medium md-text ml-1"
              sx={{
                color: '#485fc7',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              onClick={() => {
                if (shipmentById?.shipmentNumber) {
                  dispatch(downloadCarrierLabel(shipmentById?.shipmentNumber));
                }
              }}
            >
              Download the carrier AWB
            </Box>
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default BlueDartInformation;
